import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { RejectCallDialogComponent } from './reject-call-dialog.component';
import { RejectCallFlowService } from '../../telephony/services/reject-call-flow.service';
import type { RejectFlowStep, RejectReasonOption } from './models/reject-flow.model';

const HOST_OWNER = 'status-toolbar' as const;

/**
 * Body-level host for the reject dialog of a flow started **off the AgentDesktop route** (the
 * always-present `AgentStatusToolbarComponent` toast).
 *
 * Why this exists instead of rendering the dialog inside the toolbar: LeptonX's
 * `.lpx-toolbar-container` is `position: absolute; z-index: 1021`, which makes it a **stacking
 * context**. Anything rendered inside the toolbar is painted at 1021 relative to its siblings no
 * matter how high its own z-index is, so the backdrop's `--acx-z-app-modal` (8060) would be trapped
 * — the side menu (`#lpx-wrapper .lpx-sidebar-container`, 8050), body-appended PrimeNG overlays
 * (8000/8100) and the CDK overlay container (9999) would all still paint on top of the "modal",
 * leaving the app chrome un-dimmed and clickable behind it.
 *
 * `AppComponent` renders this host as a sibling of `<abp-dynamic-layout>`, outside every LeptonX
 * stacking context, which is the same pattern already used by `ConfirmDialogHostComponent`,
 * `UserSkillDialogComponent` and `AssignQueueDialogComponent`.
 *
 * `AgentDesktopComponent` keeps rendering its own dialog inline — it is a page component, not part
 * of the chrome, and owner gating keeps exactly one of the two surfaces active.
 */
@Component({
  selector: 'app-reject-call-dialog-host',
  standalone: true,
  imports: [RejectCallDialogComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './reject-call-dialog-host.component.html',
})
export class RejectCallDialogHostComponent {
  private readonly rejectFlow = inject(RejectCallFlowService);

  // Owner-gated: a flow started by AgentDesktopComponent renders in that page's own template.
  readonly step = computed<RejectFlowStep>(() => this.rejectFlow.stepFor(HOST_OWNER));
  readonly selectedReason = computed<string | null>(() =>
    this.rejectFlow.isOwnedBy(HOST_OWNER) ? this.rejectFlow.selectedReason() : null,
  );
  readonly reasonOptions = computed<readonly RejectReasonOption[]>(() =>
    this.rejectFlow.isOwnedBy(HOST_OWNER) ? this.rejectFlow.reasonOptions() : [],
  );
  readonly callerLabel = computed<string | null>(() =>
    this.rejectFlow.isOwnedBy(HOST_OWNER) ? this.rejectFlow.callerLabel() : null,
  );
  /** Reason list still loading — the dialog is on screen and genuinely busy. */
  readonly busy = computed(
    () => this.rejectFlow.isOwnedBy(HOST_OWNER) && this.rejectFlow.loadingReasons(),
  );

  onDismiss(): void {
    this.rejectFlow.cancel();
  }

  onProceedToReasonSelection(): void {
    this.rejectFlow.proceedToReasonSelection();
  }

  onSelectReason(reason: string): void {
    this.rejectFlow.selectReason(reason);
  }

  onConfirmReject(): void {
    this.rejectFlow.confirm();
  }
}
